/**
 * Description: This component will generate a Tab Panel for editing a caseload
 *              It is meant to be used in a tab <Tab.Panels> tag and will produce
 *              a <Tab.Panel> tag.
 */
import React, { Fragment, RefObject, useEffect, useState } from "react";
import moment from "moment";
import TreatmentPlan from "../../api/models/treatmentPlan";
import { Tab } from "@headlessui/react";
import User from "../../api/models/user";
import { FieldInput } from "../fieldInput";
import DatePicker from 'react-datepicker';
//import { isDate } from "../../common/utils";
import Caseload from "../../api/models/caseload";
import StudentStatus from "../../api/models/studentstatus";
import CaseloadUpdateRequest from '../../api/requests/caseloadUpdateRequest';
import Frequency from "../../api/models/frequency";
import Esy from "../../api/models/esy";
import TherapistRole from "../../api/models/therapistrole";
import { getStatusByName } from "../../common/utils";
import Therapist from "../../api/models/therapist";
import { useAsync } from "react-async";
import { selectOption } from "../../types/selectOption";
import { getCaseloadTreatmentOptionsByCaseloadId } from "../../api/services/treatmentService";
import { getCaseloadTreatmentRulesByCaseloadId } from "../../api/services/treatmentRulesService";
import DisciplineGroupRule from "../../api/models/disciplineGroupRule";

interface CaseloadRef {
  toJson(): CaseloadUpdateRequest
}

interface EditStudentTabPanelProps {
  caseload: Caseload;
  currentUser?: User;
  currentTab?: number;
  ref?: RefObject<CaseloadRef>;
  sendToParent: React.Dispatch<React.SetStateAction<CaseloadUpdateRequest>>;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  iepStartDate: Date | undefined;
  iepEndDate: Date | undefined;
  setIepStartDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setIepEndDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  treatmentPlans?: TreatmentPlan[];
  treatmentsAssigned: selectOption[];
  setTreatmentsAssigned: React.Dispatch<React.SetStateAction<selectOption[]>>;
  studentStatus: string;
  setStudentStatus: React.Dispatch<React.SetStateAction<string>>;
  trigger: boolean;
  data: EditStudentTabPanelData;
  setTreatmentOption: React.Dispatch<React.SetStateAction<number | undefined>>;
}

interface EditStudentTabPanelData {
  frequencies?: Frequency[];
  studentStatuses?: any;
  therapistRoles?: any;
  treatmentPlans?: TreatmentPlan[];
  caseloads?: Caseload[];
  esy?: Esy[];
}

const fetchTreatment = async ({ caseloadId }: any) => {
  return await getCaseloadTreatmentOptionsByCaseloadId(caseloadId);
};

const fetchTreatmentRules = async ({ caseloadId }: any) => {
  return await getCaseloadTreatmentRulesByCaseloadId(caseloadId);
}

const treatmentIEP = 1;
const treatment504 = 2;
const tpTypeIndividual = 1;
const tpTypeGroup = 2;
const tpTypeConsult = 3;

const EditStudentTabPanel = ({
  caseload,
  currentUser,
  currentTab,
  sendToParent,
  trigger,
  setTrigger,
  data,
  iepStartDate,
  iepEndDate,
  setIepStartDate,
  setIepEndDate,
  treatmentPlans,
  treatmentsAssigned,
  setTreatmentsAssigned,
  studentStatus,
  setStudentStatus,
  setTreatmentOption
}: EditStudentTabPanelProps) => {
  const [studentId, setStudentId] = useState<number>(caseload?.studentId ?? 0);
  const caseloadId = caseload.caseloadId;
  const [esy, setEsy] = useState<number | undefined>(caseload?.esyId ?? 0);
  const [caseloadStartDate, setCaseloadStartDate] = useState<Date | undefined>(caseload && caseload.caseloadStartDate ? caseload.caseloadStartDate : undefined);
  const [caseloadEndDate, setCaseloadEndDate] = useState<Date | undefined>();
  const [disciplineEndDate, setDisciplineEndDate] = useState<Date | undefined>();
  const [individual, setIndividual] = useState<boolean>(false);
  const [group, setGroup] = useState<boolean>(false);
  const [consult, setConsult] = useState<boolean>(false);
  const [individualFrequency, setIndividualFrequency] = useState<number | undefined>(0);
  const [groupFrequency, setGroupFrequency] = useState<number | undefined>(0);
  const [consultFrequency, setConsultFrequency] = useState<number | undefined>(0);
  const [individualMinutes, setIndividualMinutes] = useState<number>(0);
  const [groupMinutes, setGroupMinutes] = useState<number | undefined>(0);
  const [consultMinutes, setConsultMinutes] = useState<number | undefined>(0);
  const [treatmentvals, setTreatmentvals] = useState<selectOption[]>([]);
  const [taskSet, setTaskSet] = useState<number>(0);
  const [studentStatusId, setStudentStatusId] = useState<number>(caseload?.studentStatusId ?? -1);
  const [therapistRole, setTherapistRole] = useState<number>(caseload?.therapistRoleId ?? -1);
  const [treatment, setTreatment] = useState<number | undefined>(caseload?.treatmentId ?? 0);
  const [isMyCaseload, setIsMyCaseload] = useState<boolean>();
  const [treatmentsBound, setTreatmentsBound] = useState<boolean>(false);
  const [treatmentRules, setTreatmentRules] = useState<DisciplineGroupRule[]>([])
  const [showTreatmentPlans, setShowTreatmentPlans] = useState<boolean>(false);
  const [treatmentDatesRequired, setTreatmentDatesRequired] = useState<boolean>(false);

  /**
   * Determines whether this is the current user's caseload
   * @returns boolean
   */
  const getIsMyCaseload = () => currentUser?.userId === caseload?.therapist?.userId;

  /**
   * Updates the isMyCaseload state depending if this is the user's caseload
   */
  useEffect(() => {
    setIsMyCaseload(currentUser?.userId === caseload?.therapist?.userId)
  }, [currentTab]);

  /**
   * Initially sets the data for this specific caseload
   */
  useEffect(() => {
    //Set up our caseload hooks
    if (caseload) {
      setDisciplineEndDate(caseload?.disciplineEndDate);
      if (caseload?.caseloadStartDate) {
        setCaseloadStartDate(moment(caseload?.caseloadStartDate.toString()).toDate());
      }

      if (caseload?.caseloadEndDate)
        setCaseloadEndDate(moment(caseload?.caseloadEndDate?.toString()).toDate());

      setTreatment(caseload?.treatmentId ?? 0);
      setStudentId(caseload?.student?.studentId ?? 0);
      setStudentStatusId(caseload?.studentStatusId ?? getStatusByName(caseload.studentStatus));

      setTherapistRole(caseload?.therapistRole?.therapistRoleId ?? -1);
      setEsy(caseload?.esyId ?? caseload.studentDiscipline?.esyId ?? caseload.studentDiscipline?.esy?.esyId ?? 0);

      const individualPlan = caseload.treatmentPlans.find(x => x.treatmentPlanTypeId === tpTypeIndividual);
      const groupPlan = caseload.treatmentPlans.find(x => x.treatmentPlanTypeId === tpTypeGroup);
      const consultPlan = caseload.treatmentPlans.find(x => x.treatmentPlanTypeId === tpTypeConsult);

      if (individualPlan) {
        setIndividual(individualPlan !== undefined && individualPlan !== null);
        setIndividualFrequency(individualPlan?.treatmentFrequencyId ?? 0)
        setIndividualMinutes(individualPlan?.treatmentPlanDuration ?? 0);
      }

      if (groupPlan) {
        setGroup(groupPlan !== undefined && groupPlan !== null);
        setGroupFrequency(groupPlan?.treatmentFrequencyId ?? 0);
        setGroupMinutes(groupPlan?.treatmentPlanDuration ?? 0);
      }

      if (consultPlan) {
        setConsult(consultPlan !== undefined && consultPlan !== null);
        setConsultFrequency(consultPlan?.treatmentFrequencyId ?? 0);
        setConsultMinutes(consultPlan?.treatmentPlanDuration ?? 0);
      }
    }
  }, [caseload]);

  /**
   * Gets and sets up the treatment data (IEP, 504, etc)
   */
  useAsync({
    promiseFn: fetchTreatment, caseloadId,
    onResolve: (r) => {
      const tvals: selectOption[] = r.data.treatments.map((g) => ({ value: g.treatmentId, label: g.name }));

      if (tvals.every(x => x.value !== treatment)) {
        let empty: selectOption = {
          value: undefined,
          label: '',
        }

        tvals.unshift(empty)
        setTreatment(undefined);
      }

      setTreatmentvals(tvals);
    }
  })

  useAsync({
    promiseFn: fetchTreatmentRules, caseloadId,
    onResolve: (r) => setTreatmentRules(r.data.rules)
  })

  /**
   * Treatment rules are updated when the page loads and after the caseload loads.
   * We will use this as a trigger to show the treatment plans area, if one is set
   */
  useEffect(() => {
    if (treatmentRules.length > 0) {
      let rules = treatmentRules?.find(x => x.treatmentId === (!treatment || treatment < 1 ? caseload.treatmentId : treatment));
      let showPlans = rules?.isPlanRequired ?? false;

      setShowTreatmentPlans(showPlans);
    }
  }, [treatmentRules]);

  /**
   * Generates a CaseloadUpdateRequest object for this specific caseload
   *
   * @return {CaseloadUpdateRequest} The generated CaseloadUpdateRequest object.
   */
  const generateRequest = (): CaseloadUpdateRequest => {
    //Generate the caseload
    const caseloadUpdate: CaseloadUpdateRequest = {
      id: caseload.caseloadId,
      treatmentId: treatment,
      treatmentPlans: caseload.treatmentPlans,
      iepStartDate,
      iepEndDate,
      esyId: esy,
      caseloadStartDate,
      caseloadEndDate,
      studentId,
      studentStatus,
      studentStatusId,
      group,
      individual,
      consult,
      individualFrequency,
      consultFrequency,
      groupFrequency,
      individualMinutes,
      consultMinutes,
      groupMinutes,
      therapistRoleId: therapistRole,
      treatmentDatesRequired: treatmentDatesRequired
    }

    return caseloadUpdate;
  }

  /**
   * This effect will listen for our trigger to send to the parent
   * This call comes from the parent when it calls setTrigger
   */
  useEffect(() => {
    //If this is not one of the current therapist's caseloads, then exit
    if (!getIsMyCaseload()) return;

    //Generate our request
    const request = generateRequest();

    //Send the changes
    setTimeout(() => sendToParent(request), 100);
  }, [trigger]);

  /**
   * IEP/504 Reset Functions
   */
  const resetTreatments = () => {
    setTreatmentsAssigned([]);

    resetIep();
  }

  const reset504Dates = () => {
    setIepStartDate(undefined);
    setIepEndDate(undefined);
  }

  const resetIep = () => {
    setIndividualFrequency(0);
    setGroupFrequency(0);
    setConsultFrequency(0);

    setIndividual(false);
    setGroup(false);
    setConsult(false);

    setIndividualMinutes(0);
    setGroupMinutes(0);
    setConsultMinutes(0);
  }

  //Prepare some variables to make the code more concise
  const startDate = (caseload && caseload?.caseloadStartDate && caseload?.caseloadStartDate !== undefined && caseload?.caseloadStartDate !== null) ? moment(caseload?.caseloadStartDate.toString()).toDate() : undefined;
  const iepStart = (caseload && caseload?.iepStartDate && caseload?.iepStartDate !== undefined && caseload?.iepStartDate !== null) ? moment(caseload?.iepStartDate.toString()).toDate() : undefined;
  const iepEnd = (caseload && caseload?.iepEndDate && caseload?.iepEndDate !== undefined && caseload?.iepEndDate !== null) ? moment(caseload?.iepEndDate.toString()).toDate() : undefined;

  //Generate assigned therapists list to keep our JSX cleaner
  const assignedTherapistsJsx =
    <div className="col-span-2 tab-content-assigned-therapists">
      <label className='primary'>Assigned Therapists</label>
      <ul className="list-none">
        {/* Change this so the li key does not use i. This is a no no for React development. */}
        {caseload?.assignedTherapists?.map((t: Therapist, i: number) =>
          <li key={`assigned-therapist-${caseload.assignedTherapists?.length}-${caseload.caseloadId}-${t.disciplineId}-${t?.therapistId}-${currentTab}-${t.discipline?.name.replace(/[^a-zA-Z ]/g, "")}`} className="grid grid-cols-2">
            <span className="col-span-1 list-left">{`${t?.user?.firstName} ${t?.user?.lastName}`}</span>
            <span className="col-span-1 list-right">{`${t?.therapistRole?.name ?? ''}`}</span>
          </li>
        )}
      </ul>
    </div>;

  const handleTreatmentChanged = (treatmentId: number) => {
    setTreatment(treatmentId)
    setTreatmentOption(treatmentId)
    let rules = treatmentRules?.find(x => x.treatmentId === treatmentId);
    let showPlans = rules?.isPlanRequired ?? false;
    let datesRequired = rules?.isDatesRequired ?? false;
    setShowTreatmentPlans(showPlans);
    if (!showPlans) {
      setIndividual(showPlans);
      setIndividualFrequency(0);
      setIndividualMinutes(0);

      setGroup(showPlans);
      setGroupFrequency(0);
      setGroupMinutes(0);

      setConsult(showPlans);
      setConsultFrequency(0);
      setConsultMinutes(0);
    }

    setTreatmentDatesRequired(datesRequired);
  }

  return (<>
    <Tab.Panel>
      <div className='grid grid-cols-6 gap-6 mb-6'>
        <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
          <label className='primary'>Discipline</label>
          <p>{caseload?.disciplineName ?? ''}</p>
        </div>

        {/* Student Status */}
        <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
          <label className='primary'>Student Status</label>
          {isMyCaseload &&
            <select
              onChange={(e) => {
                if (e.target.value.toString() === '0') {
                  setStudentStatus(' -- Choose Student Status --');
                  setStudentStatusId(0);
                  return;
                };

                if (
                  isMyCaseload ||
                  (currentUser?.therapists?.some(x => x.discipline?.disciplineId === caseload?.therapist?.discipline?.disciplineId))
                ) {
                  const status = data?.studentStatuses?.find((s: StudentStatus) => s.studentStatusId === parseInt(e.target.value));
                  setStudentStatus(status?.name ?? '');
                  setStudentStatusId(parseInt(e.target.value));

                  if (status?.name !== 'On Caseload') {
                    resetTreatments();
                  }

                  resetIep();
                  reset504Dates();

                  setTrigger(current => !current);

                  if (status.name !== 'On Caseload') {
                    //setStudentTreatments([]);
                  }
                }
              }}
              className='block primary'
              disabled={!isMyCaseload}
              value={studentStatusId === 0 ? studentStatusId : data.studentStatuses.find((s: StudentStatus) => s.name === studentStatus) !== undefined ? data.studentStatuses.find((s: StudentStatus) => s.name === studentStatus).studentStatusId ?? '' : ''}
            >
              <option value='0'> -- Choose Student Status --</option>
              {data?.studentStatuses &&
                data?.studentStatuses?.map((t: StudentStatus) =>
                  <option key={`${caseload.caseloadId}-student-status-${t.studentStatusId}`} value={t.studentStatusId}>{t.name}</option>)}
            </select>
          }

          {!isMyCaseload && <div>{studentStatus ?? 'N/A'}</div>}
        </div>

        {/* Initial Start Date */}
        <div className='col-span-6 sm:col-span-6 lg:col-span-2'>
          <label className='primary'>Initial Start Date</label>
          <p>{caseload?.disciplineStartDate ? moment(caseload?.disciplineStartDate).format('MM/DD/YYYY') : (moment(caseload?.caseloadStartDate).format('MM/DD/YYYY') ?? '')}</p>
        </div>

        {isMyCaseload &&
          <>
            <div className="grid grid-cols-6 col-span-6 tab-content-inner sm:col-span-6 lg:col-span-4">
              {isMyCaseload &&
                <div className='col-span-6 mr-4 sm:col-span-6 lg:col-span-3' style={{ width: '97%', marginLeft: 0, marginRight: '1rem' }}>
                  {/* Caseload Start Date */}
                  <label className='primary'>
                    Caseload Start Date<span className='pl-1 text-red-500'>*</span>
                  </label>
                  <DatePicker
                    className='block w-3 mt-1 primary'
                    selected={caseloadStartDate}
                    disabled={!isMyCaseload}
                    onChange={(date: Date) => {
                      //if (!isMyCaseload) return;
                      setCaseloadStartDate(date!)
                      setTimeout(() => setTrigger(current => !current), 500);
                    }}
                  />
                </div>
              }
              {!(isMyCaseload) &&
                <div className='col-span-6 sm:col-span-6 lg:col-span-2'>{startDate}</div>
              }

              {/* Caseload End Date */}
              <div className='col-span-3 sm:col-span-3 lg:col-span-2.2' style={{ width: '96%', marginLeft: '1rem' }}>
                <label className='primary'>Caseload End Date</label>
                <DatePicker
                  disabled={!isMyCaseload}
                  className='block mt-1 primary'
                  selected={caseloadEndDate}
                  onChange={(date: Date) => {
                    //if (!isMyCaseload) return;
                    setCaseloadEndDate(date!)
                    setTrigger(current => !current);
                  }}
                  minDate={caseloadStartDate}
                />
              </div>

              {/* Therapist Type/Role */}
              {isMyCaseload &&
                <div className='col-span-3 sm:col-span-3 lg:col-span-2.2 lg:mt-4' style={{ width: '97%', marginLeft: 0, marginRight: '1rem' }}>
                  <label className='primary'>Therapist Type</label>
                  <select
                    className='block primary'
                    disabled={!isMyCaseload}
                    value={therapistRole ?? 0}
                    onChange={(e) => {
                      if (!isMyCaseload) return;
                      setTherapistRole(parseInt(e.target.value))
                      setTrigger(current => !current);
                    }}
                  >
                    <option value='0'> -- Choose Therapist Type --</option>
                    {data?.therapistRoles &&
                      data?.therapistRoles?.map((t: TherapistRole) =>
                        <option key={`${caseload.caseloadId}-therapist-role-${t.therapistRoleId}`} value={t.therapistRoleId}>{t.name}</option>)}
                  </select>
                </div>
              }
              {!isMyCaseload && <div>{data.therapistRoles.find((x: TherapistRole) => x.therapistRoleId === therapistRole) ?? 'N/A'}</div>}

              {/* ESY Eligible */}
              {isMyCaseload &&
                <div className='col-span-3 sm:col-span-3 lg:col-span-2.2 lg:ml-4 lg:mt-4'>
                  <label className='primary'>ESY Eligible <span className='pl-1 text-red-500'>*</span></label>
                  <select
                    className='block primary'
                    value={esy ?? 0}
                    onChange={(e) => {
                      if (!isMyCaseload) return;
                      setEsy(parseInt(e.target.value))
                      setTrigger(current => !current);
                    }}
                  >
                    <option value='0'> -- Choose ESY --</option>
                    {data?.esy?.sort((a, b) => a.name.localeCompare(b.name)).map((g) => (
                      <option
                        key={`${caseload.caseloadId}-esy-${g.esyId}`}
                        value={g.esyId}>{g.name}</option>
                    ))}
                  </select>
                </div>
              }
              {!isMyCaseload && <div>{data?.esy?.find((x: Esy) => x.esyId === esy) ?? 'N/A'}</div>}
            </div>

            {/* Assigned Therapists */}
            {assignedTherapistsJsx}
          </>
        }
      </div>

      {/* Treatment Via */}
      <div className={isMyCaseload ? `` : `grid grid-cols-6 gap-6 mb-6`}>
        <div className={!isMyCaseload ? `grid grid-cols-6 col-span-6 tab-content-inner sm:col-span-6 lg:col-span-4` : `grid grid-cols-6 gap-6 mb-6`}>
          {studentStatus === 'On Caseload' && (
            <Fragment>
              <div className={`col-span-6 sm:col-span-6 ${isMyCaseload ? 'lg:col-span-2' : `lg:col-span-3 mr-4`}`}>
                <label className='primary'>Treatment Via<span className='pl-1 text-red-500'>*</span></label>
                <select
                  className='block border-gray-300 rounded-md shadow-sm'
                  value={treatment}
                  onChange={(e) => {
                    handleTreatmentChanged(Number(e.target.value));
                    setTrigger(current => !current)
                  }}
                >
                  {treatmentvals.map((treatment) => (
                    <option key={treatment.value} value={treatment.value}>
                      {treatment.label}
                    </option>
                  ))}
                </select>
              </div>
            </Fragment>
          )}

          {disciplineEndDate && (
            <div className={`col-span-6 sm:col-span-6 ${isMyCaseload ? 'lg:col-span-2' : `lg:col-span-3`}`}>
              <label className='text-red-500 primary'>Discharge Date</label>
              <p className='text-red-500'>
                {disciplineEndDate && moment(disciplineEndDate).isValid() ? moment(disciplineEndDate).format('MM/DD/YYYY') : disciplineEndDate}
              </p>
            </div>
          )}

          {/* Show IEP Dates inside this container only if this IS my caseload */}
          {isMyCaseload ? <>
            {studentStatus === 'On Caseload' && (
              <div className={`col-span-6 sm:col-span-6 ${isMyCaseload ? 'lg:col-span-2' : `lg:col-span-3`}`}>
                <label className='primary'>
                  Treatment Start Date{treatmentDatesRequired && isMyCaseload ? <span className='pl-1 text-red-500'>*</span> : ""}
                </label>
                {isMyCaseload &&
                  <DatePicker
                    className='block mt-1 primary'
                    selected={iepStartDate}
                    disabled={!isMyCaseload}
                    required={treatmentDatesRequired && isMyCaseload}
                    onChange={(e: Date) => {
                      setIepStartDate(e!)

                      if (!moment(e).isValid() || e === undefined) return;
                      setIepEndDate(moment(e).add(1, 'year').subtract(1, 'day').toDate());

                      setTrigger(current => !current);
                    }}
                  />
                }
                {!isMyCaseload && <div className="ml-0">{moment(iepStartDate ?? iepStart).format('MM/DD/YYYY') ?? 'N/A'}</div>}
              </div>
            )}

            {/* IEP End Date */}
            {studentStatus === 'On Caseload' && (
              <div className={`col-span-6 ml-3 sm:col-span-6 ${isMyCaseload ? 'lg:col-span-2' : `lg:col-span-3 mr-4`}`}>
                <label className='primary'>
                  Treatment End Date{treatmentDatesRequired && isMyCaseload ? <span className='pl-1 text-red-500'>*</span> : ""}
                </label>
                {isMyCaseload &&
                  <DatePicker
                    className='block mt-1 primary'
                    selected={moment(iepEndDate).isValid() ? iepEndDate : moment().add(1, 'year').subtract(1, 'day').toDate()}
                    disabled={!isMyCaseload}
                    required={treatmentDatesRequired && isMyCaseload}
                    onChange={(e: Date) => {
                      if (moment(e).isSame(moment().add(1, 'year').subtract(1, 'day').toDate())) return;

                      setIepEndDate(e!)
                      setTrigger(current => !current);
                    }}
                  />
                }
                {!isMyCaseload && <><div></div><div>{moment(iepEndDate).format('MM/DD/YYYY') ?? 'N/A'}</div></>}
              </div>
            )}
          </> : <></>}
        </div>

        {/* Assigned Therapists */}
        {!isMyCaseload ? assignedTherapistsJsx : <></>}

        {/* Show IEP Dates outside of the above container only if this IS NOT my caseload */}
        {!isMyCaseload ?
          <div className="grid grid-cols-6 col-span-6 tab-content-inner sm:col-span-6 lg:col-span-4">
            {studentStatus === 'On Caseload' && (
              <div className={`col-span-3 sm:col-span-3`}>
                <label className='primary'>
                  Treatment Start Date<span className='pl-1 text-red-500'>*</span>
                </label>
                <div className="ml-0">{moment(iepStartDate ?? iepStart).format('MM/DD/YYYY') ?? 'N/A'}</div>
              </div>
            )}

            {/* IEP End Date */}
            {studentStatus === 'On Caseload' &&
              <div className={`col-span-3 sm:col-span-3 ml-3.5`}>
                <label className='primary'>Treatment End Date</label>
                <div>{moment(iepEndDate).format('MM/DD/YYYY') ?? 'N/A'}</div>
              </div>}
          </div> : <></>}

        {/* Individual, Group, Consult Conditional Field */}
        <div className="grid grid-cols-6 col-span-6 tab-content-inner sm:col-span-6 lg:col-span-4">
          {/* Individual */}
          {studentStatus === 'On Caseload' && (showTreatmentPlans || (treatment && treatment > 0 && treatment !== treatment504))
            /* && (individual || group || consult)*/ /* || on504 === true) && taskSet > 0*/ &&
            (
              <Fragment>
                <div className='col-span-6 sm:col-span-6'>
                  <h1 className='mb-3'>Treatment Plan</h1>

                  <div className='flex items-start'>
                    <div className='flex items-center h-5'>
                      <input
                        id='individual'
                        name='individual'
                        type='checkbox'
                        checked={individual}
                        disabled={!isMyCaseload}
                        readOnly={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setIndividual(e.currentTarget.checked)
                          setTrigger(current => !current);
                        }}
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor='individual'>Individual</label>
                    </div>
                  </div>
                </div>

                {/* Individual Frequency */}
                {individual && (
                  <Fragment>
                    <div className='col-span-6 ml-6 sm:col-span-6 lg:col-span-2'>
                      <label className='primary'>Frequency</label>
                      <select
                        disabled={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setIndividualFrequency(Number(e.target.value))
                          setTrigger(current => !current);
                        }}
                        className='block mt-1 primary'
                        value={individualFrequency ?? 0}
                      >
                        <option value='0'> -- Frequency--</option>
                        {data?.frequencies?.sort((a, b) => a.loadOrder - b.loadOrder).map((g) => (
                          <option
                            key={`${caseload.caseloadId}-frequency-${g.treatmentFrequencyId}`}
                            value={g.treatmentFrequencyId}>{g.treatmentFrequencyName}</option>
                        ))}
                      </select>
                    </div>
                    <div className='col-span-6 mt-1 ml-6 sm:col-span-6 lg:col-span-2'>
                      <FieldInput
                        name='individualMinutes'
                        label='Minutes'
                        disabled={!isMyCaseload}
                        readonly={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setIndividualMinutes(Number(e.target.value.replace(/\D/g, "")) ?? 0)
                          setTrigger(current => !current);
                        }}
                        value={individualMinutes ? individualMinutes.toString() : 0}
                      />
                    </div>
                  </Fragment>
                )}

                {/* Group */}
                <div className='col-span-6 mt-2 mb-1 sm:col-span-6'>
                  <div className='flex items-start'>
                    <div className='flex items-center h-5'>
                      <input
                        id='group'
                        name='group'
                        type='checkbox'
                        checked={group}
                        disabled={!isMyCaseload}
                        readOnly={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setGroup(e.currentTarget.checked)
                          setTrigger(current => !current);
                        }}
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor='group'>Group</label>
                    </div>
                  </div>
                </div>

                {/* Frequency */}
                {group && (
                  <Fragment>
                    <div className='col-span-6 ml-6 sm:col-span-6 lg:col-span-2'>
                      <label className='primary'>Frequency</label>
                      <select
                        disabled={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setGroupFrequency(Number(e.target.value))
                          setTrigger(current => !current);
                        }}
                        className='block mt-1 primary'
                        value={groupFrequency ?? 0}
                      >
                        <option value='0'> -- Frequency--</option>
                        {data?.frequencies?.sort((a, b) => a.loadOrder - b.loadOrder).map((g) => (
                          <option
                            key={`${caseload.caseloadId}-tf-${g.treatmentFrequencyId}`}
                            value={g.treatmentFrequencyId}>{g.treatmentFrequencyName}</option>
                        ))}
                      </select>
                    </div>
                    <div className='col-span-6 mt-1 ml-6 sm:col-span-6 lg:col-span-2'>
                      <FieldInput
                        name='groupMinutes'
                        label='Minutes'
                        disabled={!isMyCaseload}
                        readonly={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setGroupMinutes(Number(e.target.value.replace(/\D/g, "")));
                          setTrigger(current => !current);
                        }}
                        value={groupMinutes ? groupMinutes.toString() : ''}
                      />
                    </div>
                  </Fragment>
                )}

                {/* Consult */}
                <div className='col-span-6 mt-2 mb-1 sm:col-span-6'>
                  <div className='flex items-start'>
                    <div className='flex items-center h-5'>
                      <input
                        id='consult'
                        name='consult'
                        type='checkbox'
                        checked={consult}
                        readOnly={!isMyCaseload}
                        disabled={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setConsult(e.currentTarget.checked)
                          setTrigger(current => !current);
                        }}
                      />
                    </div>
                    <div className='ml-3 text-sm'>
                      <label htmlFor='consult'>Consult</label>
                    </div>
                  </div>
                </div>

                {consult && (
                  <Fragment>
                    <div className='col-span-6 ml-6 sm:col-span-6 lg:col-span-2'>
                      <label className='primary'>Freqency</label>
                      <select
                        disabled={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setConsultFrequency(Number(e.target.value))
                          setTrigger(current => !current);
                        }}
                        className='block mt-1 primary'
                        value={consultFrequency ?? 0}
                      >
                        <option value='0'> -- Frequency--</option>
                        {data?.frequencies?.sort((a, b) => a.loadOrder - b.loadOrder).map((g) => (
                          <option
                            key={`${caseload.caseloadId}-fd-${g.treatmentFrequencyId}`}
                            value={g.treatmentFrequencyId}>{g.treatmentFrequencyName}</option>
                        ))}
                      </select>
                    </div>
                    <div className='col-span-6 mt-1 ml-6 sm:col-span-6 lg:col-span-2'>
                      <FieldInput
                        name='consultMinutes'
                        label='Minutes'
                        disabled={!isMyCaseload}
                        readonly={!isMyCaseload}
                        onChange={(e) => {
                          if (!isMyCaseload) return;
                          setConsultMinutes(Number(e.target.value.replace(/\D/g, "")))
                          setTrigger(current => !current);
                        }}
                        value={consultMinutes ? consultMinutes.toString() : ''}
                      />
                    </div>
                  </Fragment>
                )}
              </Fragment>
            )}
        </div>
      </div>
    </Tab.Panel>
  </>);
}
export default EditStudentTabPanel;
